import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Printer } from 'lucide-react'
import { getBalanceSheetGaap, getIncomeStatementGaap, getMonthEnd, getTrialBalanceGaap } from '../../api/accounting'
import { getCompany } from '../../api/settings'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { StatusBadge } from '../../components/PageParts'
import { fmtDay, localToday } from '../../utils/format'
import { money } from '../sales/salesShared'
import { monthName, recentMonths, thisYm } from './accountingShared'
import { ReportsTabs, StatementHead, StatementTable, balanceRows, incomeRows } from './reportsShared'
import { printPage, useAutoPrint } from '../../utils/print'

const monthEnd = (ym) => {
  const [y, m] = ym.split('-').map(Number)
  return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`
}

// Month-end report pack: the month's income statement, balance sheet and
// trial balance (GAAP), one per page, ready to print or save as PDF.
export default function PeriodReportPrint() {
  const [params, setParams] = useSearchParams()
  const months = useMemo(() => recentMonths(24), [])
  const ym = /^\d{4}-\d{2}$/.test(params.get('period') || '') ? params.get('period') : thisYm()
  const [company, setCompany] = useState(null)
  const [data, setData] = useState(null)
  useAutoPrint(!!data)
  const [error, setError] = useState('')

  useEffect(() => { getCompany().then(setCompany).catch(() => setCompany({})) }, [])

  useEffect(() => {
    const start = `${ym}-01`
    const end = monthEnd(ym)
    setError('')
    setData(null)
    Promise.all([
      getIncomeStatementGaap({ start, end, compare: 'previous' }),
      getBalanceSheetGaap({ as_of: end, compare: 'month_end' }),
      getTrialBalanceGaap({ as_of: end }),
      getMonthEnd(ym).catch(() => null),
    ])
      .then(([income, balance, trial, close]) => setData({ income, balance, trial, closed: !!close?.closed }))
      .catch((e) => setError(e.message || 'Could not prepare the report pack.'))
  }, [ym])

  const name = company?.company_name || ''
  const logoPath = company?.logo_path || ''
  const logo = logoPath ? (logoPath.startsWith('http') ? logoPath : `${import.meta.env.VITE_API_URL || ''}${logoPath}`) : ''
  const label = monthName(ym)
  // Column headings such as "Oct 2026".
  const short = (d) => new Date(`${d.slice(0, 7)}-01T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })

  return (
    <div className="ci-page fs-page">
      <ReportsTabs />
      <div className="ci-header-row fs-noprint">
        <div className="ci-title-block">
          <div>
            <h1>Month-end report pack <HelpTip text="The month’s income statement, balance sheet and trial balance together, one per page, ready to print or save as PDF." /></h1>
            <div className="ci-title-meta">For the owner, the bank or the accountant.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-primary" disabled={!data} onClick={() => printPage()}><Printer size={15} />Print or save PDF</button>
        </div>
      </div>

      <div className="ci-toolbar fs-noprint">
        <select className="ci-select" value={ym} onChange={(e) => setParams({ period: e.target.value }, { replace: true })} aria-label="Month">
          {(months.includes(ym) ? months : [ym, ...months]).map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
        </select>
        {data && (data.closed
          ? <StatusBadge label="Month closed" tone="good" />
          : <StatusBadge label="Month still open: figures can change" tone="warn" />)}
        {data && !data.closed && <Link className="acc-link-btn" to={`/accounting/month-end?month=${ym}`}>Close the month</Link>}
      </div>

      {error && <ErrorBanner message={error} />}

      {!data ? (!error && <Spinner label="Preparing the report pack…" />) : (
        <div className="fs-pack">
          <section className="fs-card fs-pack-cover">
            {logo && <img className="fs-pack-logo" src={logo} alt="" />}
            <div className="fs-company">{name}</div>
            <div className="fs-pack-title">Month-end report pack</div>
            <div className="fs-sub">{label}{data.closed ? '' : ' · provisional, the month is still open'}</div>
            <ol className="fs-pack-contents">
              <li>Income statement</li>
              <li>Balance sheet</li>
              <li>Trial balance</li>
            </ol>
            <div className="fs-sub">Prepared {fmtDay(localToday())}</div>
          </section>

          <section className="fs-card fs-pack-section">
            <StatementHead company={name} title="Income statement" sub={`For the month of ${label}`} />
            <StatementTable rows={incomeRows(data.income)} currentLabel={short(ym)} priorLabel={data.income.prior_period ? short(data.income.prior_period.start) : ''} glRange={[`${ym}-01`, monthEnd(ym)]} />
          </section>

          <section className="fs-card fs-pack-section">
            <StatementHead company={name} title="Balance sheet" sub={`As of ${fmtDay(data.balance.as_of)}`} />
            <StatementTable rows={balanceRows(data.balance)} currentLabel={fmtDay(data.balance.as_of)} priorLabel={data.balance.prior_as_of ? fmtDay(data.balance.prior_as_of) : ''} glRange={[null, data.balance.as_of]} />
            {!data.balance.balanced && <p className="fs-foot">Out of balance by {money(Math.abs(data.balance.difference))}.</p>}
          </section>

          <section className="fs-card fs-pack-section">
            <StatementHead company={name} title="Trial balance" sub={`As of ${fmtDay(data.trial.as_of)}`} />
            <table className="fs-table">
              <thead><tr><th>Account</th><th className="hide-sm">Statement section</th><th className="num">Debit</th><th className="num">Credit</th></tr></thead>
              <tbody>
                {data.trial.rows.map((r) => (
                  <tr key={r.code} className="fs-line">
                    <td>{r.code} · {r.name}</td>
                    <td className="hide-sm fs-muted">{r.section}</td>
                    <td className="num">{r.debit ? money(r.debit) : ''}</td>
                    <td className="num">{r.credit ? money(r.credit) : ''}</td>
                  </tr>
                ))}
                <tr className="fs-grand">
                  <td>Total</td><td className="hide-sm" />
                  <td className="num">{money(data.trial.total_debit)}</td>
                  <td className="num">{money(data.trial.total_credit)}</td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>
      )}
    </div>
  )
}
